
import React, { useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { formatLocalDate } from "../../../utils/formatLocalDate";
import { buildMonthGrid } from "./slotGrid";

const MONTH_LABEL = (year, month) =>
  new Date(year, month, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

/**
 * A horizontally-scrolling strip of a month's days — the Bookings dashboard's
 * date picker. Unlike `SlotMonthCalendar`'s grid (which is about *publishing*
 * a whole month of availability, so every day needs to be visible at once),
 * this is about picking one day to look at, so a strip that scrolls to the
 * selected day reads less busy at the width the dashboard gives it.
 *
 * Built on the same `buildMonthGrid` as `SlotMonthCalendar` — flattened, since
 * a strip has no use for week rows — so both stay in agreement about what
 * counts as closed or past.
 *
 * @param {Object} props
 * @param {number} props.year
 * @param {number} props.month - 0-indexed.
 * @param {(year: number, month: number) => void} props.onMonthChange
 * @param {Date|null} props.selectedDate
 * @param {(date: Date) => void} props.onSelectDate
 * @param {Record<string, Array<Object>>} props.markedByDate - Keyed `YYYY-MM-DD`;
 *   any day with a non-empty array gets a dot.
 * @param {boolean} [props.busy] - A fetch is in flight; drives a small spinner
 *   next to the month label rather than gating the strip itself.
 * @param {boolean} [props.allowPast=false] - Make days before today selectable.
 * @param {boolean} [props.allowClosed=false] - Make Sundays selectable too.
 * @param {string} [props.markLabel="has bookings"] - Screen-reader suffix for a
 *   marked day.
 */
export const DayStripCalendar = ({
  year,
  month,
  onMonthChange,
  selectedDate,
  onSelectDate,
  markedByDate,
  busy = false,
  allowPast = false,
  allowClosed = false,
  markLabel = "has bookings",
}) => {
  const days = useMemo(
    () => buildMonthGrid(year, month).flat().filter(Boolean),
    [year, month]
  );
  const selectedKey = selectedDate ? formatLocalDate(selectedDate) : null;
  const selectedRef = useRef(null);

  useEffect(() => {
    // Bring the selection into view without fighting the user's own scroll
    // position on every re-render — "nearest" is a no-op once it's visible.
    // jsdom (Jest) has no scrollIntoView implementation at all.
    if (typeof selectedRef.current?.scrollIntoView === "function") {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedKey]);

  const step = (delta) => {
    const next = new Date(year, month + delta, 1);
    onMonthChange(next.getFullYear(), next.getMonth());
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex flex-shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous month"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-bc-text-2 transition-colors hover:bg-light-orange/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <span
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-bc-text"
          aria-live="polite"
        >
          {MONTH_LABEL(year, month)}
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-bc-faint" aria-hidden="true" />
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next month"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-bc-text-2 transition-colors hover:bg-light-orange/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div
        role="listbox"
        aria-label="Pick a date"
        className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth py-1"
      >
        {days.map((cell) => {
          const marked = (markedByDate?.[cell.key] || []).length > 0;
          const disabled =
            (cell.isClosed && !allowClosed) || (cell.isPast && !allowPast);
          const isSelected = cell.key === selectedKey;

          let stateClass;
          if (disabled) {
            stateClass = "cursor-not-allowed border-transparent bg-light-gray text-bc-faintest";
          } else if (isSelected) {
            stateClass = "border-transparent bg-orange-primary text-white";
          } else {
            stateClass = "border-bc-hairline bg-white text-bc-text hover:border-orange-primary/50 hover:bg-light-orange/40";
          }

          return (
            <button
              key={cell.key}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={disabled}
              onClick={() => onSelectDate(cell.date)}
              className={`relative flex h-16 w-14 flex-shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1 ${stateClass}`}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                {cell.date.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase()}
              </span>
              <span className="text-base font-bold">{cell.day}</span>
              {marked ? (
                <span
                  aria-hidden="true"
                  className={`absolute bottom-1.5 h-1 w-1 rounded-full ${
                    isSelected ? "bg-white" : "bg-orange-primary"
                  }`}
                />
              ) : null}
              {marked ? <span className="sr-only">, {markLabel}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};
